import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Copy, Download, Share2, Eye, EyeOff, ChevronRight, Check } from 'lucide-react';
import { foundation } from '../../../ui';
import { bg } from '../../../ui/theme/semanticTokens';
import { alphaOf } from '../../../utils/ui/colorAlpha';
import MfaRecoveryAckRow from './MfaRecoveryAckRow';
import { ACTION_BEAT_ATTR } from '../../../ui/actionBeatMotion';
import useHiddenWhileBackgrounded from './useHiddenWhileBackgrounded';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { mfaStepTitleStyle, mfaStepSubStyle } from './mfaStyles';
import {
  canShareRecoveryCodes,
  shareRecoveryCodes,
  copyRecoveryCodesToClipboard,
  triggerRecoveryCodesDownload,
} from '../../../utils/auth/recoveryCodeShare';

const f = foundation;
const fc = f.color;

const destsWrap = { border: `1px solid ${fc.neutral.border}`, borderRadius: f.radii.md, overflow: 'hidden', marginBottom: f.spacePx[3] };
// Every row after the first carries a 1px divider (was: only "Ver códigos").
const destRow = (first) => ({ display: 'flex', alignItems: 'center', gap: f.spacePx[3], width: '100%', border: 'none', borderTop: first ? 'none' : `1px solid ${fc.neutral.border}`, background: fc.neutral.surface, padding: `14px ${f.spacePx[3]}`, minHeight: 52, cursor: 'pointer', textAlign: 'left', fontFamily: f.fontFamily.sans, boxSizing: 'border-box' });
const wellStyle = (done) => ({ flexShrink: 0, width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: done ? fc.semantic.successSoft : fc.brand.primaryMuted, color: done ? fc.semantic.success : fc.brand.primary, transition: 'background-color 200ms ease, color 200ms ease' });
// Label + sub are BLOCK spans on their own lines — as inline spans they ran
// together on device ("Descargar archivoUn .txt…").
const destTextWrap = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 };
const destLabel = { display: 'block', fontSize: '0.9375rem', fontWeight: f.fontWeight.semibold, color: fc.neutral.text, lineHeight: 1.3 };
const destSub = (done) => ({ display: 'block', fontSize: f.fontSize.sm, color: done ? fc.semantic.success : fc.neutral.textMuted, fontWeight: done ? f.fontWeight.semibold : f.fontWeight.normal, lineHeight: 1.35 });
const gridWrapStyle = { position: 'relative', padding: `0 ${f.spacePx[3]} ${f.spacePx[3]}` };
const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `2px ${f.spacePx[3]}`, backgroundColor: fc.neutral.surfaceMuted, borderRadius: f.radii.md, padding: f.spacePx[3] };
const gridItemStyle = { display: 'flex', alignItems: 'baseline', gap: f.spacePx[2], padding: '5px 0', fontFamily: f.fontFamily.mono, fontSize: f.fontSize.sm, whiteSpace: 'nowrap', color: fc.neutral.text };
const gridIndexStyle = { fontFamily: f.fontFamily.sans, fontSize: f.fontSize.xs, color: fc.neutral.textMuted, minWidth: 14, textAlign: 'right' };
const veilStyle = { position: 'absolute', inset: 0, backdropFilter: 'blur(9px)', WebkitBackdropFilter: 'blur(9px)', backgroundColor: alphaOf(bg.surface, 0.55), display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: f.radii.md };
const veilPillStyle = { display: 'flex', alignItems: 'center', gap: f.spacePx[2], fontWeight: f.fontWeight.semibold, fontSize: f.fontSize.sm, color: fc.neutral.text, backgroundColor: bg.surface, border: `1px solid ${fc.neutral.border}`, borderRadius: 999, padding: `${f.spacePx[2]} ${f.spacePx[3]}` };

/**
 * RD-SEC-015 flow H — "B · Save first": the job is to SAVE the codes, so a
 * save destination comes before reading them. LOCKED CONTRACT (also consumed
 * by B2's `MfaRegenerateCodesModal`):
 * `MfaStepRecoveryCodes({ codes, compact, acknowledged, onAcknowledgedChange, userEmail })`.
 *
 * Rows: `compact` (phone/tablet) with `navigator.share` → "Guardar en otra
 * app" (share sheet); otherwise (web, or compact without Share support) →
 * "Descargar archivo". Both surfaces always get "Copiar" and the "Ver
 * códigos" disclosure (2-col numbered grid, codes never wrap). A completed
 * save flips its row to done AND nudges the acknowledgment tick — the tick
 * itself (`MfaRecoveryAckRow`) is ALWAYS a manual, explicit action; a save
 * never auto-ticks it. While the tab/app is backgrounded
 * (`useHiddenWhileBackgrounded`) the grid is blurred behind a veil.
 */
export default function MfaStepRecoveryCodes({ codes, compact, acknowledged, onAcknowledgedChange, userEmail }) {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const [viewOpen, setViewOpen] = useState(false);
  const [savedKeys, setSavedKeys] = useState({});
  const [nudge, setNudge] = useState(false);
  const hidden = useHiddenWhileBackgrounded();

  useEffect(() => {
    if (acknowledged) setNudge(false);
  }, [acknowledged]);

  const markSaved = (key) => {
    setSavedKeys((cur) => ({ ...cur, [key]: true }));
    if (!acknowledged) setNudge(true);
  };

  const shareSupported = canShareRecoveryCodes();
  const showShareRow = compact && shareSupported;

  const handleShare = async () => {
    const result = await shareRecoveryCodes(codes, {
      title: intl.formatMessage({ id: 'mfaStepper.codes.fileHead', defaultMessage: 'CrewVex recovery codes' }),
    });
    // AbortError (the user's own cancel) never flips the row to done (AC 15).
    if (result.ok) markSaved('share');
  };

  const handleDownload = () => {
    triggerRecoveryCodesDownload(
      codes,
      intl.formatMessage({ id: 'mfaStepper.codes.fileHead', defaultMessage: 'CrewVex recovery codes' }),
      { email: userEmail || '' },
    );
    markSaved('download');
  };

  const handleCopy = () => {
    void copyRecoveryCodesToClipboard(codes).then((ok) => {
      if (ok) markSaved('copy');
    });
  };

  const destRows = [];
  if (showShareRow) {
    destRows.push({
      key: 'share',
      icon: Share2,
      label: intl.formatMessage({ id: 'mfaStepper.codes.share', defaultMessage: 'Save to another app' }),
      sub: intl.formatMessage({ id: 'mfaStepper.codes.shareSub', defaultMessage: 'Passwords, Notes or Files' }),
      onClick: handleShare,
    });
  } else {
    destRows.push({
      key: 'download',
      icon: Download,
      label: intl.formatMessage({ id: 'mfaStepper.codes.dl', defaultMessage: 'Download file' }),
      sub: intl.formatMessage({ id: 'mfaStepper.codes.dlSub', defaultMessage: 'A .txt with all 10 codes' }),
      onClick: handleDownload,
    });
  }
  destRows.push({
    key: 'copy',
    icon: Copy,
    label: intl.formatMessage({ id: 'mfaStepper.codes.copy', defaultMessage: 'Copy' }),
    sub: intl.formatMessage({ id: 'mfaStepper.codes.copySub', defaultMessage: 'To paste anywhere' }),
    onClick: handleCopy,
  });

  return (
    // The codes are the result of a verified step (enrollment step 3, the
    // regenerate modal's screen 2): a resting touch pop-up recentres for them.
    <div {...{ [ACTION_BEAT_ATTR]: 'done' }}>
      <p style={mfaStepTitleStyle}>{intl.formatMessage({ id: 'twoFactor.recovery.title', defaultMessage: 'Save your recovery codes' })}</p>
      <p style={mfaStepSubStyle}>{intl.formatMessage({ id: 'mfaStepper.codes.sub', defaultMessage: 'Use one if you lose your phone. Shown only now.' })}</p>

      <div style={destsWrap}>
        {destRows.map(({ key, icon: Icon, label, sub, onClick }, index) => {
          const done = Boolean(savedKeys[key]);
          return (
            <button key={key} type="button" style={destRow(index === 0)} onClick={onClick} data-testid={`mfa-codes-row-${key}`}>
              <span style={wellStyle(done)} aria-hidden>{done ? <Check size={16} strokeWidth={2.6} /> : <Icon size={16} strokeWidth={2} />}</span>
              <span style={destTextWrap}>
                <span style={destLabel} data-testid={`mfa-codes-row-label-${key}`}>{label}</span>
                <span style={destSub(done)} data-testid={`mfa-codes-row-sub-${key}`}>{done ? intl.formatMessage({ id: 'mfaStepper.codes.done', defaultMessage: 'Done' }) : sub}</span>
              </span>
            </button>
          );
        })}
        <button type="button" style={destRow(false)} onClick={() => setViewOpen((v) => !v)} aria-expanded={viewOpen} data-testid="mfa-codes-row-view">
          <span style={wellStyle(false)} aria-hidden><Eye size={16} strokeWidth={2} /></span>
          <span style={destTextWrap}>
            <span style={destLabel}>{intl.formatMessage({ id: 'mfaStepper.codes.view', defaultMessage: 'Show codes' })}</span>
            <span style={destSub(false)}>{intl.formatMessage({ id: 'mfaStepper.codes.viewSub', defaultMessage: 'To write them down' })}</span>
          </span>
          <ChevronRight data-testid="mfa-codes-view-chevron" size={16} aria-hidden style={{ flexShrink: 0, transform: viewOpen ? 'rotate(90deg)' : 'none', transition: reducedMotion ? 'none' : 'transform 200ms ease', color: fc.neutral.iconMuted }} />
        </button>
        {viewOpen ? (
          <div style={gridWrapStyle}>
            <div style={gridStyle}>
              {codes.map((codeValue, i) => (
                <div key={codeValue} style={gridItemStyle}>
                  <span style={gridIndexStyle} aria-hidden>{i + 1}</span>{codeValue}
                </div>
              ))}
            </div>
            {hidden ? (
              <div style={veilStyle} aria-hidden>
                <span style={veilPillStyle}><EyeOff size={14} strokeWidth={2.2} />{intl.formatMessage({ id: 'mfaStepper.codes.hidden', defaultMessage: 'Hidden while the app is in the background' })}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <MfaRecoveryAckRow acknowledged={acknowledged} onChange={onAcknowledgedChange} nudge={nudge} reducedMotion={reducedMotion} />
    </div>
  );
}
